import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import * as BS from 'react-bootstrap';
import * as BSEx from '../../../BootstrapExtends';
import Box from '../../../atoms/Box';
import DateRangePicker from '../DateRangePicker';
import CustomReportPanel from '../CustomReportPanel';
import SpreadsheetReportConfigs from '../../../../models/SpreadsheetReportConfigs/index.js';
import {
  PeriodOptions,
  IntervalOptions,
  DayOfWeekOptions,
  StatusValues,
} from '../../../../enums/SpreadsheetReportConfigs';
import 'react-datepicker/dist/react-datepicker.css';
import * as Yup from 'yup';
import { Formik } from 'formik';
import {
  PERIOD_OPTION_VALUES,
  INTERVAL_OPTION_VALUES,
  STATUS_VALUES,
  EXPORT_TYPE_OPTION_VALUES,
  DEFAULT_PANEL_VALUE,
  MAX_PANELS_PER_SHEET,
  DND_ITEM_TYPES,
} from '../../../../constants/SpreadsheetReportConfigs.js';
import Sheet from '../../../../models/SpreadsheetReportConfigs/Sheet';
import { generateUniqueId } from '../../../../lib/StringUtils.js';
import { withDnDProvider } from '../../../../lib/dnd.jsx';
import ConversionPoint from '../../../../models/SpreadsheetReportConfigs/ConversionPoint';
import { preventEnterKeySubmit } from '../../../../lib/FormUtils';
import './index.scss';
import ImportSpreadsheetConfigModal from '../../Modal/ImportSpreadsheetConfigModal';
import {
  GenericDndContainer,
  GenericDndItem,
} from '../../../molecules/GenericDnd';
import { reorderItem } from '../../../../lib/DndUtils.js';

const scope =
  'frontend.components.SpreadsheetReportConfigs.Form.CustomReportForm';

const propTypes = {
  projectId: PropTypes.string.isRequired,
  isEdit: PropTypes.bool,
  spreadsheetReportConfigs: PropTypes.shape(SpreadsheetReportConfigs.propTypes),
  sheetList: PropTypes.arrayOf(PropTypes.shape(Sheet.propTypes)),
  conversionPoints: PropTypes.arrayOf(
    PropTypes.shape(ConversionPoint.propTypes)
  ),
  limitReached: PropTypes.bool,
  channelOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  onUpdateSpreadsheetButtonClick: PropTypes.func.isRequired,
  onUpdateSheetButtonClick: PropTypes.func.isRequired,
  onImportSpreadsheetConfigStart: PropTypes.func.isRequired,
  onSyncSpreadsheetReportConfigs: PropTypes.func.isRequired,
  onCancelButtonClick: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

const CustomReportForm = ({
  projectId,
  isEdit,
  spreadsheetReportConfigs,
  sheetList = [],
  conversionPoints,
  limitReached,
  channelOptions = [],
  onUpdateSpreadsheetButtonClick,
  onUpdateSheetButtonClick,
  onImportSpreadsheetConfigStart,
  onSyncSpreadsheetReportConfigs,
  onCancelButtonClick,
  onSubmit,
}) => {
  const isSpreadsheetSelected = Boolean(spreadsheetReportConfigs.sheetId);
  const sanitizedPanels = useMemo(() => {
    const sortedPanelKeys =
      (spreadsheetReportConfigs.panels?.sortedPanelKeys || []).length > 0
        ? spreadsheetReportConfigs.panels.sortedPanelKeys
        : sheetList.map((sheet) => sheet.title);
    const items = { ...(spreadsheetReportConfigs.panels?.items || {}) };

    sortedPanelKeys.forEach((sheetTitle) => {
      if (!Array.isArray(items[sheetTitle])) {
        items[sheetTitle] = [];
      }
    });

    return {
      items,
      sortedPanelKeys,
    };
  }, [spreadsheetReportConfigs.panels, sheetList]);

  const [selectedSheetTitle, setSelectedSheetTitle] = useState(
    sanitizedPanels.sortedPanelKeys[0] || ''
  );
  const [newlyAddedPanelIds, setNewlyAddedPanelIds] = useState(new Set());
  const [expandedPanelId, setExpandedPanelId] = useState(
    newlyAddedPanelIds.size > 0 ? [...newlyAddedPanelIds][0] : null
  );
  const [
    showImportSpreadsheetConfigModal,
    setShowImportSpreadsheetConfigModal,
  ] = useState(false);
  const [importBaseValues, setImportBaseValues] = useState(null);

  useEffect(() => {
    const sortedKeys = sanitizedPanels.sortedPanelKeys;
    if (!sortedKeys.length) {
      if (selectedSheetTitle) {
        setSelectedSheetTitle('');
      }
      return;
    }
    if (!selectedSheetTitle || !sortedKeys.includes(selectedSheetTitle)) {
      setSelectedSheetTitle(sortedKeys[0]);
    }
  }, [sanitizedPanels.sortedPanelKeys, selectedSheetTitle]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        sheetId: Yup.string().required().max(255),
        name: Yup.string().required().trim().max(255),
        status: Yup.string().required(),
        period: Yup.string().required(),
        startDate: Yup.string().when('period', {
          is: PERIOD_OPTION_VALUES.custom_period,
          then: (schema) =>
            schema
              .required()
              .typeError(I18n.t(`${scope}.messages.dateTypeError`)),
          otherwise: (schema) => schema.nullable(),
        }),
        endDate: Yup.string()
          .when('period', {
            is: PERIOD_OPTION_VALUES.custom_period,
            then: (schema) =>
              schema
                .required()
                .typeError(I18n.t(`${scope}.messages.dateTypeError`)),
            otherwise: (schema) => schema.nullable(),
          })
          .test(
            'is-after-start',
            I18n.t(`${scope}.messages.endDateIsAfterStartDate`),
            function (value) {
              const { startDate, period } = this.parent;
              if (period !== PERIOD_OPTION_VALUES.custom_period) return true;
              if (!startDate || !value) return true;
              return value >= startDate;
            }
          ),
        panels: Yup.object({
          items: Yup.object().shape(
            Object.fromEntries(
              sanitizedPanels.sortedPanelKeys.map((sheetTitle) => [
                sheetTitle,
                Yup.array().of(
                  Yup.object({
                    id: Yup.string().required(),
                    name: Yup.string().required().max(255),
                    type: Yup.string().required(),
                    column: Yup.string()
                      .required()
                      .max(
                        3,
                        I18n.t(
                          `${scope}.messages.columnMustBeUppercaseAlphabetAndLimitTo3Characters`
                        )
                      )
                      .matches(
                        /^[A-Z]+$/,
                        I18n.t(
                          `${scope}.messages.columnMustBeUppercaseAlphabetAndLimitTo3Characters`
                        )
                      ),
                    row: Yup.number().required().positive(),
                    content: Yup.string().when('type', {
                      is: EXPORT_TYPE_OPTION_VALUES.string,
                      then: (schema) => schema.required().trim(),
                      otherwise: (schema) => schema.nullable(),
                    }),
                    numberOfMaxLines: Yup.number().when('type', {
                      is: EXPORT_TYPE_OPTION_VALUES.table,
                      then: (schema) => schema.required().positive().max(5000),
                      otherwise: (schema) => schema.nullable(),
                    }),
                    period: Yup.string().when('type', {
                      is: EXPORT_TYPE_OPTION_VALUES.table,
                      then: (schema) => schema.required(),
                      otherwise: (schema) => schema.nullable(),
                    }),
                    startedOn: Yup.string().when('period', {
                      is: PERIOD_OPTION_VALUES.custom_period,
                      then: (schema) =>
                        schema
                          .required()
                          .typeError(I18n.t(`${scope}.messages.dateTypeError`)),
                      otherwise: (schema) => schema.nullable(),
                    }),
                    endedOn: Yup.string()
                      .when('period', {
                        is: PERIOD_OPTION_VALUES.custom_period,
                        then: (schema) =>
                          schema
                            .required()
                            .typeError(
                              I18n.t(`${scope}.messages.dateTypeError`)
                            ),
                        otherwise: (schema) => schema.nullable(),
                      })
                      .test(
                        'is-after-start',
                        I18n.t(`${scope}.messages.endDateIsAfterStartDate`),
                        function (value) {
                          const { startedOn, period } = this.parent;
                          if (period !== PERIOD_OPTION_VALUES.custom_period)
                            return true;
                          if (!startedOn || !value) return true;
                          return value >= startedOn;
                        }
                      ),
                    columns: Yup.array().of(
                      Yup.object({
                        content: Yup.string().required(),
                        name: Yup.string().required(),
                      })
                    ),
                    filters: Yup.array().of(
                      Yup.array().of(
                        Yup.object({
                          targetColumn: Yup.string().required(),
                          condition: Yup.string().required(),
                          value: Yup.string().when('condition', {
                            is: (value) =>
                              value === 'is_null' || value === 'is_not_null',
                            then: (schema) => schema.nullable(),
                            otherwise: (schema) => schema.required(),
                          }),
                        })
                      )
                    ),
                    sorts: Yup.array().of(
                      Yup.object({
                        targetColumn: Yup.string().required(),
                        isAsc: Yup.bool().required(),
                      })
                    ),
                  })
                ),
              ])
            )
          ),
        }),
        schedules: Yup.object({
          interval: Yup.string(),
          date: Yup.number()
            .typeError(I18n.t(`${scope}.messages.selectTypeError`))
            .when('interval', {
              is: (value) => value === INTERVAL_OPTION_VALUES.monthly,
              then: (schema) => schema.required().min(1).max(31),
              otherwise: (schema) => schema.nullable(),
            }),
          dayOfWeek: Yup.string().when('interval', {
            is: INTERVAL_OPTION_VALUES.weekly,
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.nullable(),
          }),
          hour: Yup.number()
            .typeError(I18n.t(`${scope}.messages.selectTypeError`))
            .when('interval', {
              is: INTERVAL_OPTION_VALUES.once,
              then: (schema) => schema.nullable(),
              otherwise: (schema) => schema.required().min(0).max(23),
            }),
        }),
      }),
    [sheetList, sanitizedPanels]
  );

  const initialValues = {
    sheetId: spreadsheetReportConfigs.sheetId,
    name: spreadsheetReportConfigs.name,
    status: spreadsheetReportConfigs.status,
    period: spreadsheetReportConfigs.period,
    startDate: spreadsheetReportConfigs.startDate,
    endDate: spreadsheetReportConfigs.endDate,
    panels: sanitizedPanels,
    schedules: spreadsheetReportConfigs.schedules,
  };

  const createNewPanel = ({ panelName, period, startedOn, endedOn }) => {
    return {
      ...DEFAULT_PANEL_VALUE,
      id: generateUniqueId(),
      name: panelName,
      period: period,
      startedOn: startedOn,
      endedOn: endedOn,
    };
  };

  const addPanelToState = ({
    newPanel,
    setFieldValue,
    currentPanels,
    insertIndex = null,
  }) => {
    const nextPanels = [...currentPanels];
    if (insertIndex !== null) {
      nextPanels.splice(insertIndex, 0, newPanel);
    } else {
      nextPanels.push(newPanel);
    }

    setNewlyAddedPanelIds((prev) => new Set([...prev, newPanel.id]));
    setExpandedPanelId(newPanel.id);
    setFieldValue(`panels.items[${selectedSheetTitle}]`, nextPanels);
  };

  const handleAddPanel = (setFieldValue, values) => {
    if (!selectedSheetTitle) return;
    const currentPanels = values.panels.items[selectedSheetTitle] || [];
    if (currentPanels.length >= MAX_PANELS_PER_SHEET) return;
    const newPanelValues = createNewPanel({
      panelName: `パネル${currentPanels.length + 1}`,
      period: values.period,
      startedOn: values.startDate,
      endedOn: values.endDate,
    });

    addPanelToState({ newPanel: newPanelValues, setFieldValue, currentPanels });
  };

  const movePanel = ({ dragIndex, hoverIndex, setFieldValue, values }) => {
    if (dragIndex === hoverIndex || !selectedSheetTitle) return;

    const currentPanels = values.panels.items[selectedSheetTitle] || [];
    const newPanels = reorderItem({
      list: currentPanels,
      dragIndex,
      hoverIndex,
    });

    setFieldValue(`panels.items[${selectedSheetTitle}]`, newPanels);
  };

  const handleDeletePanel = (id, setFieldValue, values) => {
    const updatedPanelsItems = { ...values.panels.items };

    Object.keys(updatedPanelsItems).forEach((title) => {
      if (!Array.isArray(updatedPanelsItems[title])) {
        updatedPanelsItems[title] = [];
        return;
      }
      updatedPanelsItems[title] = updatedPanelsItems[title].filter((panel) => {
        return panel.id !== id;
      });
    });

    setNewlyAddedPanelIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });

    setFieldValue('panels.items', updatedPanelsItems);
  };

  const handleCopyPanel = (id, setFieldValue, values) => {
    if (!selectedSheetTitle) return;
    const currentPanels = values.panels.items[selectedSheetTitle] || [];
    if (currentPanels.length >= MAX_PANELS_PER_SHEET) return;

    const originalPanelIndex = currentPanels.findIndex(
      (panel) => panel.id === id
    );

    // Methods findIndex return -1 if value not found
    if (originalPanelIndex === -1) return;

    const originalPanel = currentPanels[originalPanelIndex];
    const newPanel = {
      ...cloneDeep(originalPanel),
      id: generateUniqueId(),
      name: `${originalPanel.name} ${I18n.t(`${scope}.copySuffix`)}`,
      column: '',
      row: '',
      numberOfMaxLines: '',
    };

    addPanelToState({
      newPanel,
      setFieldValue,
      currentPanels,
      insertIndex: originalPanelIndex + 1,
    });
  };

  const handlePanelChange = (
    selectedSheetTitle,
    panelIndex,
    fieldName,
    value,
    setFieldValue
  ) => {
    setFieldValue(
      `panels.items.${selectedSheetTitle}[${panelIndex}].${fieldName}`,
      value
    );
  };

  const handlePanelBlur = (
    selectedSheetTitle,
    panelIndex,
    fieldName,
    setFieldTouched
  ) => {
    setFieldTouched(
      `panels.items.${selectedSheetTitle}[${panelIndex}].${fieldName}`,
      true
    );
  };

  const handleIntervalChange = (value, setFieldValue) => {
    if (value === INTERVAL_OPTION_VALUES.monthly) {
      setFieldValue('schedules.date', '');
      setFieldValue('schedules.dayOfWeek', undefined);
      setFieldValue('schedules.hour', '');
    } else if (value === INTERVAL_OPTION_VALUES.weekly) {
      setFieldValue('schedules.date', undefined);
      setFieldValue('schedules.dayOfWeek', '');
      setFieldValue('schedules.hour', '');
    } else if (value === INTERVAL_OPTION_VALUES.once) {
      setFieldValue('schedules.date', undefined);
      setFieldValue('schedules.dayOfWeek', undefined);
      setFieldValue('schedules.hour', undefined);
    } else {
      setFieldValue('schedules.date', undefined);
      setFieldValue('schedules.dayOfWeek', undefined);
      setFieldValue('schedules.hour', '');
    }
    setFieldValue('schedules.interval', value);
  };

  const handleUpdateSheetButtonClick = (values) => {
    onUpdateSheetButtonClick(values);
  };

  const handleSheetChange = (sheetTitle) => {
    setExpandedPanelId(null);
    setSelectedSheetTitle(sheetTitle);
  };

  const columnToIndex = (column) => {
    if (!column) return null;
    let result = 0;
    for (let i = 0; i < column.length; i += 1) {
      const code = column.charCodeAt(i);
      if (code < 65 || code > 90) return null;
      result = result * 26 + (code - 64);
    }
    return result;
  };

  const getPanelOutputRect = (panel) => {
    if (!panel) return null;
    const startCol = columnToIndex(panel.column);
    const startRow = Number(panel.row);
    if (!startCol || !startRow) return null;

    let columnCount = 1;
    let rowCount = 1;
    if (panel.type === EXPORT_TYPE_OPTION_VALUES.table) {
      columnCount = panel.columns?.length || 0;
      if (columnCount <= 0) return null;
      const maxLines = Number(panel.numberOfMaxLines);
      if (!maxLines) return null;
      rowCount = maxLines + (panel.showHeaderRow ? 1 : 0);
    }

    const endCol = startCol + columnCount - 1;
    const endRow = startRow + rowCount - 1;
    return { startCol, endCol, startRow, endRow };
  };

  const buildOverlapErrors = (values) => {
    const panelsBySheet = values.panels?.items || {};
    const overlapMessage = I18n.t(`${scope}.messages.panelOutputRangeOverlaps`);
    const errors = { panels: { items: {} } };
    let hasOverlap = false;

    Object.entries(panelsBySheet).forEach(([sheetTitle, panels]) => {
      if (!Array.isArray(panels) || panels.length === 0) return;
      const rects = panels.map((panel) => getPanelOutputRect(panel));
      const overlapped = new Set();

      for (let i = 0; i < rects.length; i += 1) {
        const rectA = rects[i];
        if (!rectA) continue;
        for (let j = i + 1; j < rects.length; j += 1) {
          const rectB = rects[j];
          if (!rectB) continue;
          const isOverlapping =
            rectA.startCol <= rectB.endCol &&
            rectA.endCol >= rectB.startCol &&
            rectA.startRow <= rectB.endRow &&
            rectA.endRow >= rectB.startRow;
          if (isOverlapping) {
            overlapped.add(i);
            overlapped.add(j);
          }
        }
      }

      if (overlapped.size > 0) {
        hasOverlap = true;
        errors.panels.items[sheetTitle] = panels.map((panel, index) => {
          if (!overlapped.has(index)) return {};
          return { column: overlapMessage, row: overlapMessage };
        });
      }
    });

    return hasOverlap ? errors : null;
  };

  const handleSubmit = (values, formikHelpers) => {
    const overlapErrors = buildOverlapErrors(values);
    if (overlapErrors) {
      formikHelpers.setErrors(overlapErrors);
      Object.entries(overlapErrors.panels.items).forEach(
        ([sheetTitle, panelErrors]) => {
          panelErrors.forEach((panelError, index) => {
            if (panelError?.column) {
              formikHelpers.setFieldTouched(
                `panels.items.${sheetTitle}[${index}].column`,
                true,
                false
              );
            }
            if (panelError?.row) {
              formikHelpers.setFieldTouched(
                `panels.items.${sheetTitle}[${index}].row`,
                true,
                false
              );
            }
          });
        }
      );
      formikHelpers.setSubmitting(false);
      return;
    }
    onSubmit(values);
  };

  const cloneDeep = (value) => {
    if (typeof structuredClone === 'function') {
      return structuredClone(value);
    }
    return JSON.parse(JSON.stringify(value));
  };

  const handleImportSpreadsheetConfigButtonClick = (values) => {
    setImportBaseValues(cloneDeep(values));
    setShowImportSpreadsheetConfigModal(true);
  };

  const handleImportSpreadsheetConfigModalHide = () => {
    setShowImportSpreadsheetConfigModal(false);
    setImportBaseValues(null);
  };

  const handleImportSpreadsheetConfig = (selectedConfig) => {
    if (!selectedConfig) {
      handleImportSpreadsheetConfigModalHide();
      return;
    }
    const baseValues = importBaseValues || spreadsheetReportConfigs;
    onSyncSpreadsheetReportConfigs(cloneDeep(baseValues));
    onImportSpreadsheetConfigStart(selectedConfig);
    handleImportSpreadsheetConfigModalHide();
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({
        values,
        errors,
        touched,
        isValid,
        isSubmitting,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        setFieldTouched,
      }) => {
        return (
          <BS.Form
            horizontal
            onSubmit={handleSubmit}
            onKeyDown={preventEnterKeySubmit}
          >
            <Box>
              <Box.Header>
                <Box.Title>
                  {I18n.t(`${scope}.basicInfoSection.sectionTitle`)}
                </Box.Title>
              </Box.Header>

              <Box.Body>
                {/* speadsheet ID */}
                <BSEx.FormGroup>
                  <BS.Col componentClass={BS.ControlLabel} sm={2} xs={12}>
                    {I18n.t(
                      `${scope}.basicInfoSection.field.spreadsheetId.label`
                    )}
                  </BS.Col>
                  <BS.Col sm={4} xs={12}>
                    <BS.FormControl
                      bsClass='form-control'
                      placeholder={I18n.t(
                        `${scope}.basicInfoSection.field.spreadsheetId.placeholder`
                      )}
                      value={spreadsheetReportConfigs.sheetId}
                      type='text'
                      readOnly
                    />
                    {!spreadsheetReportConfigs.sheetId && (
                      <BS.HelpBlock style={{ color: '#d9534f' }}>
                        {I18n.t(
                          `${scope}.basicInfoSection.field.spreadsheetId.errorRequired`,
                          { defaultValue: '' }
                        )}
                      </BS.HelpBlock>
                    )}
                  </BS.Col>
                  <BS.Col componentClass={BS.ButtonToolbar} sm={6} xs={12}>
                    <div className='custom-report-form-button-group custom-report-form-button-group--sheet'>
                      <BS.Button
                        bsStyle='primary'
                        href={`https://docs.google.com/spreadsheets/d/${spreadsheetReportConfigs.sheetId}/edit`}
                        target='_blank'
                        type='button'
                        disabled={!isSpreadsheetSelected}
                      >
                        <i className='fa fa-external-link' />
                      </BS.Button>
                      <BS.Button
                        bsStyle='primary'
                        type='button'
                        onClick={() => onUpdateSpreadsheetButtonClick()}
                      >
                        {I18n.t(`${scope}.action.updateSpreadsheet`)}
                      </BS.Button>
                      <BS.Button
                        bsStyle='primary'
                        type='button'
                        onClick={() => handleUpdateSheetButtonClick(values)}
                        disabled={!isSpreadsheetSelected}
                      >
                        {I18n.t(`${scope}.action.updateSheet`)}
                      </BS.Button>
                      <BS.Button
                        bsStyle='primary'
                        type='button'
                        onClick={() =>
                          handleImportSpreadsheetConfigButtonClick(values)
                        }
                        disabled={!isSpreadsheetSelected}
                      >
                        {I18n.t(
                          `${scope}.action.importSpreadsheetConfigFromAnotherProject`
                        )}
                      </BS.Button>
                    </div>
                  </BS.Col>
                </BSEx.FormGroup>

                {/* spreadsheet report config name */}
                <BSEx.FormGroupInline
                  label={I18n.t(
                    `${scope}.basicInfoSection.field.spreadsheetReportConfigName.label`
                  )}
                  error={errors.name}
                  touched={touched.name}
                >
                  <BS.FormControl
                    placeholder={I18n.t(
                      `${scope}.basicInfoSection.field.spreadsheetReportConfigName.placeholder`
                    )}
                    type='text'
                    name='name'
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </BSEx.FormGroupInline>

                {/* period */}
                <BSEx.FormGroupInline
                  label={I18n.t(
                    `${scope}.sheetConfigSection.field.period.label`
                  )}
                  error={errors.period}
                  touched={touched.period}
                >
                  <BS.FormControl
                    componentClass='select'
                    name='period'
                    value={values.period}
                    onChange={(e) => {
                      handleChange(e);
                      // custom_period 以外では start_date/end_date を保持しない。
                      // 休眠日付が新規パネルや文字列パネルの日付変数 ({{start_month}} 等)
                      // に伝播するのを防ぐ。
                      if (
                        e.target.value !== PERIOD_OPTION_VALUES.custom_period
                      ) {
                        setFieldValue('startDate', null);
                        setFieldValue('endDate', null);
                      }
                    }}
                    onBlur={handleBlur}
                  >
                    {Object.entries(PeriodOptions).map(([key, option]) => (
                      <option key={key} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </BS.FormControl>
                </BSEx.FormGroupInline>

                {values.period === PERIOD_OPTION_VALUES.custom_period && (
                  <DateRangePicker
                    startDateLabel={I18n.t(
                      `${scope}.sheetConfigSection.field.startDate.label`
                    )}
                    endDateLabel={I18n.t(
                      `${scope}.sheetConfigSection.field.endDate.label`
                    )}
                    startDate={values.startDate}
                    endDate={values.endDate}
                    errors={errors}
                    touched={touched}
                    onChangeStartDate={handleChange('startDate')}
                    onChangeEndDate={handleChange('endDate')}
                    onBlurStartDate={() => setFieldTouched('startDate', true)}
                    onBlurEndDate={() => setFieldTouched('endDate', true)}
                  />
                )}
              </Box.Body>
            </Box>

            <Box>
              <Box.Header>
                <Box.Title>
                  {I18n.t(`${scope}.sheetConfigSection.sectionTitle`)}
                </Box.Title>
              </Box.Header>

              <Box.Body>
                <div className='custom-report-form-sheet-description'>
                  <p>
                    {I18n.t(`${scope}.sheetConfigSection.description.intro`)}
                  </p>
                  <ol>
                    <li>
                      {I18n.t(`${scope}.sheetConfigSection.description.step1`)}
                    </li>
                    <li>
                      {I18n.t(`${scope}.sheetConfigSection.description.step2`)}
                    </li>
                    <li>
                      {I18n.t(`${scope}.sheetConfigSection.description.step3`)}
                    </li>
                    <li>
                      {I18n.t(`${scope}.sheetConfigSection.description.step4`)}
                    </li>
                    <li>
                      {I18n.t(`${scope}.sheetConfigSection.description.step5`)}
                    </li>
                    <li>
                      {I18n.t(`${scope}.sheetConfigSection.description.step6`)}
                    </li>
                  </ol>
                </div>

                {/* current sheet id */}
                <BSEx.FormGroupInline
                  label={I18n.t(
                    `${scope}.sheetConfigSection.field.currentSheetName.label`
                  )}
                >
                  <BS.FormGroup className='custom-report-form-sheet-selector'>
                    {sanitizedPanels.sortedPanelKeys.map((sheet) => {
                      const sheetHasErrors = Boolean(
                        errors.panels?.items?.[sheet]
                      );
                      const sheetPanels = values.panels.items[sheet] || [];
                      return (
                        <BS.Radio
                          name='panel-key-radio-group'
                          key={sheet}
                          checked={selectedSheetTitle === sheet}
                          onChange={() => handleSheetChange(sheet)}
                          className={`custom-report-form-sheet-option ${
                            sheetHasErrors
                              ? 'custom-report-form-sheet-option--error'
                              : ''
                          }`}
                        >
                          <span className='custom-report-form-sheet-option-content'>
                            <span>{sheet}</span>
                            {sheetPanels.length > 0 && (
                              <BS.Badge className='custom-report-form-sheet-badge'>
                                {sheetPanels.length}
                              </BS.Badge>
                            )}
                          </span>
                        </BS.Radio>
                      );
                    })}
                  </BS.FormGroup>
                </BSEx.FormGroupInline>

                <GenericDndContainer
                  items={values.panels.items[selectedSheetTitle] || []}
                  itemType={DND_ITEM_TYPES.panel}
                  onMove={(dragIndex, hoverIndex) =>
                    movePanel({
                      dragIndex,
                      hoverIndex,
                      setFieldValue,
                      values,
                    })
                  }
                  classNames={{
                    container: 'panel-dnd-container',
                    itemWrapper: 'panel-dnd-item-wrapper',
                    dropZone: 'panel-dnd-drop-zone',
                  }}
                  renderItem={({ item: panel, index }) => {
                    const panelHasErrors = Boolean(
                      errors.panels?.items?.[selectedSheetTitle]?.[index]
                    );
                    return (
                      <GenericDndItem
                        key={panel.id}
                        index={index}
                        itemType={DND_ITEM_TYPES.panel}
                        className='panel-dnd-item'
                      >
                        {(drag) => (
                          <BS.Panel
                            style={{ marginBottom: 0 }}
                            defaultExpanded={newlyAddedPanelIds.has(panel.id)}
                            expanded={expandedPanelId === panel.id}
                            onToggle={() =>
                              setExpandedPanelId(
                                expandedPanelId === panel.id ? null : panel.id
                              )
                            }
                          >
                            <BS.Panel.Heading
                              className={`custom-report-form-panel-heading ${
                                expandedPanelId === panel.id
                                  ? 'custom-report-form-selected-panel-heading'
                                  : ''
                              }`}
                              onClick={() =>
                                setExpandedPanelId(
                                  expandedPanelId === panel.id ? null : panel.id
                                )
                              }
                            >
                              <div
                                ref={drag}
                                className='custom-report-form-panel-header-wrapper'
                              >
                                <BS.Panel.Title
                                  componentClass='h5'
                                  className='custom-report-form-panel-title'
                                >
                                  <i className='fa fa-angle-right custom-report-form-panel-expand-icon'></i>
                                  <span
                                    className={`custom-report-form-panel-title-text ${
                                      panelHasErrors
                                        ? 'custom-report-form-panel-title-text--error'
                                        : ''
                                    }`}
                                  >
                                    {panel.name}
                                  </span>
                                </BS.Panel.Title>
                                <div>
                                  <BS.Button
                                    bsStyle='default'
                                    bsSize='xs'
                                    type='button'
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleCopyPanel(
                                        panel.id,
                                        setFieldValue,
                                        values
                                      );
                                    }}
                                    style={{ marginRight: '5px' }}
                                  >
                                    <i className='fa fa-clone' />
                                  </BS.Button>
                                  <BS.Button
                                    bsStyle='danger'
                                    bsSize='xs'
                                    type='button'
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleDeletePanel(
                                        panel.id,
                                        setFieldValue,
                                        values
                                      );
                                    }}
                                  >
                                    <i className='fa fa-trash' />
                                  </BS.Button>
                                </div>
                              </div>
                            </BS.Panel.Heading>
                            <BS.Panel.Collapse>
                              <BS.Panel.Body>
                                <CustomReportPanel
                                  panel={panel}
                                  updatedSpreadsheetReportConfigs={values}
                                  errors={
                                    errors.panels?.items?.[
                                      selectedSheetTitle
                                    ]?.[index] || {}
                                  }
                                  touched={
                                    touched.panels?.items?.[
                                      selectedSheetTitle
                                    ]?.[index] || {}
                                  }
                                  onChange={(fieldName, value) =>
                                    handlePanelChange(
                                      selectedSheetTitle,
                                      index,
                                      fieldName,
                                      value,
                                      setFieldValue
                                    )
                                  }
                                  onBlur={(fieldName) =>
                                    handlePanelBlur(
                                      selectedSheetTitle,
                                      index,
                                      fieldName,
                                      setFieldTouched
                                    )
                                  }
                                  conversionPoints={conversionPoints}
                                  channelOptions={channelOptions}
                                />
                              </BS.Panel.Body>
                            </BS.Panel.Collapse>
                          </BS.Panel>
                        )}
                      </GenericDndItem>
                    );
                  }}
                />

                <BS.Button
                  bsStyle='primary'
                  type='button'
                  onClick={() => handleAddPanel(setFieldValue, values)}
                  disabled={
                    !isSpreadsheetSelected ||
                    (values.panels.items[selectedSheetTitle] || []).length >=
                      MAX_PANELS_PER_SHEET
                  }
                >
                  {I18n.t(`${scope}.action.addPanel`)}
                </BS.Button>
                {(values.panels.items[selectedSheetTitle] || []).length >=
                  MAX_PANELS_PER_SHEET && (
                  <div
                    className='custom-report-form-error-message'
                    style={{ marginTop: '8px' }}
                  >
                    {I18n.t(`${scope}.messages.panelLimitReached`, {
                      max: MAX_PANELS_PER_SHEET,
                    })}
                  </div>
                )}
              </Box.Body>
            </Box>

            <Box>
              <Box.Header>
                <Box.Title>
                  {I18n.t(`${scope}.scheduleOutputSection.sectionTitle`)}
                </Box.Title>
              </Box.Header>

              <Box.Body>
                {/* output interval */}
                <BSEx.FormGroupInline
                  label={I18n.t(
                    `${scope}.scheduleOutputSection.field.outputFrequency.label`
                  )}
                  error={errors.schedules?.interval}
                  touched={touched.schedules?.interval}
                >
                  <BS.FormControl
                    componentClass='select'
                    name='schedules.interval'
                    value={values.schedules?.interval || ''}
                    onChange={(e) =>
                      handleIntervalChange(e.target.value, setFieldValue)
                    }
                    onBlur={handleBlur}
                  >
                    {Object.entries(IntervalOptions).map(([key, option]) => (
                      <option key={key} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </BS.FormControl>
                </BSEx.FormGroupInline>

                {/* output date */}
                {values.schedules.interval ===
                  INTERVAL_OPTION_VALUES.monthly && (
                  <BSEx.FormGroupInline
                    label={I18n.t(
                      `${scope}.scheduleOutputSection.field.outputDay.label`
                    )}
                    error={errors.schedules?.date}
                    touched={touched.schedules?.date}
                  >
                    <BS.FormControl
                      componentClass='select'
                      name='schedules.date'
                      value={values.schedules.date ?? ''}
                      onChange={(e) =>
                        setFieldValue(
                          'schedules.date',
                          e.target.value ? Number(e.target.value) : ''
                        )
                      }
                      onBlur={handleBlur}
                    >
                      <option value='' disabled hidden />
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                        <option key={d} value={d}>
                          {`${d}日`}
                        </option>
                      ))}
                    </BS.FormControl>
                  </BSEx.FormGroupInline>
                )}

                {/* output date of week */}
                {values.schedules.interval ===
                  INTERVAL_OPTION_VALUES.weekly && (
                  <BSEx.FormGroupInline
                    label={I18n.t(
                      `${scope}.scheduleOutputSection.field.outputDayOfWeek.label`
                    )}
                    error={errors.schedules?.dayOfWeek}
                    touched={touched.schedules?.dayOfWeek}
                  >
                    <BS.FormControl
                      componentClass='select'
                      name='schedules.dayOfWeek'
                      value={values.schedules.dayOfWeek ?? ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      <option value='' disabled hidden />
                      {Object.entries(DayOfWeekOptions).map(([key, option]) => (
                        <option key={key} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </BS.FormControl>
                  </BSEx.FormGroupInline>
                )}

                {/* output time */}
                {values.schedules.interval !== INTERVAL_OPTION_VALUES.once && (
                  <BSEx.FormGroupInline
                    label={I18n.t(
                      `${scope}.scheduleOutputSection.field.outputTime.label`
                    )}
                    error={errors.schedules?.hour}
                    touched={touched.schedules?.hour}
                  >
                    <BS.FormControl
                      componentClass='select'
                      name='schedules.hour'
                      value={values.schedules.hour ?? ''}
                      onChange={(e) =>
                        setFieldValue(
                          'schedules.hour',
                          e.target.value === '' ? '' : Number(e.target.value)
                        )
                      }
                      onBlur={handleBlur}
                    >
                      <option value='' disabled hidden />
                      {Array.from({ length: 24 }, (_, h) => h).map((h) => (
                        <option key={h} value={h}>
                          {`${h}時`}
                        </option>
                      ))}
                    </BS.FormControl>
                  </BSEx.FormGroupInline>
                )}
                {values.schedules.interval !== INTERVAL_OPTION_VALUES.once && (
                  <BS.FormGroup>
                    <BS.Col smOffset={2} sm={10}>
                      <BS.HelpBlock
                        className='text-muted'
                        style={{ whiteSpace: 'pre-wrap' }}
                      >
                        {I18n.t(
                          `${scope}.scheduleOutputSection.messages.executionDelay`
                        )}
                      </BS.HelpBlock>
                    </BS.Col>
                  </BS.FormGroup>
                )}
              </Box.Body>
            </Box>

            <div className='custom-report-form-actions'>
              <div className='custom-report-form-actions-left'>
                <BS.ButtonToolbar>
                  <BS.Button
                    bsStyle='primary'
                    type='submit'
                    disabled={
                      !isSpreadsheetSelected || !isValid || isSubmitting
                    }
                  >
                    {isEdit
                      ? I18n.t(`${scope}.action.update`)
                      : I18n.t(`${scope}.action.create`)}
                  </BS.Button>

                  <BS.Button
                    bsStyle='default'
                    type='button'
                    onClick={onCancelButtonClick}
                  >
                    {I18n.t(`${scope}.action.cancel`)}
                  </BS.Button>
                </BS.ButtonToolbar>

                {errors.panels?.items && (
                  <span className='custom-report-form-error-message'>
                    {Object.keys(errors.panels.items)
                      .map(
                        (panelKey) =>
                          sheetList.find((sheet) => sheet.title === panelKey)
                            ?.title || panelKey
                      )
                      .join(', ')}{' '}
                    {I18n.t(`${scope}.messages.invalidValue`)}
                  </span>
                )}
              </div>

              {isEdit && (
                <BS.Button
                  bsStyle={
                    values.status === STATUS_VALUES.valid ? 'danger' : 'success'
                  }
                  type='button'
                  className='pull-right'
                  disabled={
                    initialValues.status !== STATUS_VALUES.valid && limitReached
                  }
                  onClick={() =>
                    setFieldValue(
                      'status',
                      values.status === STATUS_VALUES.valid
                        ? STATUS_VALUES.invalid
                        : STATUS_VALUES.valid
                    )
                  }
                >
                  {values.status === STATUS_VALUES.valid
                    ? StatusValues.invalid.label
                    : StatusValues.valid.label}
                </BS.Button>
              )}
            </div>

            <ImportSpreadsheetConfigModal
              show={showImportSpreadsheetConfigModal}
              onHide={handleImportSpreadsheetConfigModalHide}
              projectId={projectId}
              onImport={handleImportSpreadsheetConfig}
            />
          </BS.Form>
        );
      }}
    </Formik>
  );
};

CustomReportForm.propTypes = propTypes;

export default withDnDProvider(CustomReportForm);
